import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  Output,
  EventEmitter,
  AfterViewInit,
  ViewChildren,
  QueryList,
  ViewChild,
  ElementRef
} from '@angular/core';
import { combineLatest, Observable, Subject, switchMap } from 'rxjs';
import { Select, Store } from '@ngxs/store';
import { ActionsExecuting, actionsExecuting } from '@ngxs-labs/actions-executing';
import { CustomIcons } from '@shared/models/custom-icons';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { map, startWith, takeUntil, tap } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { setLocationQueryParams } from '@utils/other-utils';
import { Location } from '@angular/common';
import { MultiSelectOption } from '@shared/components/multi-select/models';
import { UntypedFormControl } from '@angular/forms';
import { MultiSelectWithInputComponent } from '@shared/components/multi-select-with-input/components/multi-select-with-input/multi-select-with-input.component';
import { DeclensionWordPipe } from '@shared/pipes/declension-word/declension-word.pipe';
import { LangChangeEvent, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AuthState } from '@auth/store/auth/auth.state';
import { AuthorizationDialogComponent } from '@auth/components/authorization-dialog/authorization-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { TakeUntilDestroy } from '@utils/auto-unsubscribe';
import { StartSearch } from '@shared/components/search-input/search-input.component';
import { cloneDeep } from 'lodash';
import { CatalogProviders, Categories } from '@shared/models/shared.service';
import { SharedState } from '@shared/state/shared/shared.state';
import { Locale } from '@shared/models/locale';
import { CatalogFilter, CatalogQueryParams, HandleCatalogHeaderToggleModel } from '../../models/catalog.service';
import { ChangeCatalogFilter, GetCatalogs, HandleCatalogHeaderToggle } from '../../store/catalog-table.actions';
import { CatalogTableState } from '../../store/catalog-table.state';
import { ConnectedPosition } from "@angular/cdk/overlay";

@Component({
  selector: 'app-catalog-table-header',
  templateUrl: './catalog-table-header.component.html',
  styleUrls: ['./catalog-table-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DeclensionWordPipe, TranslatePipe]
})
@TakeUntilDestroy
export class CatalogTableHeaderComponent implements OnInit, AfterViewInit {
  private _buttonTogglePrevValue = 'all';
  public componentDestroy: () => Subject<any>;
  public isMobile = false;
  public isMobileSearch = false;
  @Output() public readonly tableHeaderLoaded: EventEmitter<boolean> = new EventEmitter();
  public isOpenFilter = false;
  public isOpenCategoryFilter = false;
  public isOpenProvidersFilter = false;
  public isMobileInputShowed = false;
  public showMobileSearch = false;
  public newFavoriteRecommendedToggle = new UntypedFormControl('all');

  public categoriesFilterControl = new UntypedFormControl('');
  public providersFilterControl = new UntypedFormControl('');

  public overlayPosition: ConnectedPosition[] = this.getOverlayPosition(215);
  public filterOverlayPosition: ConnectedPosition[] = this.getOverlayPosition(60);
  public icons = CustomIcons;
  public filterContainer: 'modal-filters-container' | 'filter-container' = 'filter-container';
  @Select(actionsExecuting([GetCatalogs])) public loadingDataTable$: Observable<ActionsExecuting>;
  @Select(CatalogTableState.getCatalogFilter) public catalogFilter$: Observable<CatalogFilter>;
  @Select(CatalogTableState.getCatalogTotal) public countCatalogs$: Observable<number>;
  @Select(SharedState.getCategories) public categoriesList$!: Observable<Categories[]>;
  @Select(SharedState.getProviders) public providersList$!: Observable<CatalogProviders[]>;
  @Select(CatalogTableState.hasFilters) public hasFilters$: Observable<boolean>;
  @Select(CatalogTableState.hasMobileFilters) public hasMobileFilters$: Observable<boolean>;
  @Select(CatalogTableState.hasCategoryFilters) public hasMobileCategoryOIVFilters$: Observable<boolean>;

  public categoriesOptions$: Observable<MultiSelectOption[]> = this.makeTranslateAndFilterObserver().pipe(
    switchMap(([filters]) =>
      this.categoriesList$.pipe(
        map(categories =>
          categories.map(category => ({
            title: this.translateService.currentLang === Locale.RU_RU ? category.fullName : category.fullName_en ?? category.fullName,
            value: category.idOpenCategory,
            icon: category.image,
            imageCode: category.imageCode,
            isSelected: filters?.openCategoryIds?.includes(category.idOpenCategory) ?? false,
            subtext: `${this.getSetsWord()}: ${category.catalogsCount}`
          }))
        ),
        tap(() => {
          this.categoriesFilterControl.setValue(filters ? filters.openCategoryIds : [], { emitEvent: false });
        })
      )
    )
  );

  public providersOptions$: Observable<MultiSelectOption[]> = this.makeTranslateAndFilterObserver().pipe(
    switchMap(([filters]) =>
      this.providersList$.pipe(
        map(providerOption =>
          providerOption.map(provider => ({
            title:
              // eslint-disable-next-line no-nested-ternary
              this.translateService.currentLang === Locale.RU_RU
                ? provider.fullNameOIV
                : provider.fullNameOIV_en && provider.fullNameOIV_en.trim().length > 0
                ? provider.fullNameOIV_en
                : provider.fullNameOIV,
            value: provider.idOIV,
            isSelected: filters.providerIds?.includes(provider.idOIV) ?? false,
            subtext: `${this.getSetsWord()}: ${provider.openCatalogCount}`
          }))
        ),
        tap(() => {
          //  if (!this.isOpenProvidersFilter && filters.providerIds?.length === 0) {
          const filterProviderIds = filters ? filters.providerIds : [];
          this.providersFilterControl.setValue(filterProviderIds, { emitEvent: false });
        })
      )
    )
  );

  @ViewChildren(MultiSelectWithInputComponent) public catalogProviderFilterRef?: QueryList<MultiSelectWithInputComponent>;
  @ViewChild('mobileFav') public mobileFav: ElementRef<HTMLInputElement>;

  public breakPointObserve$ = this.breakpointObserver
    .observe(['(max-height: 800px)', '(max-width: 1650px)', '(max-width: 1070px)', '(max-width: 768px)', '(max-width: 400px)'])
    .pipe(
      map((state: BreakpointState) => {
        if (state.breakpoints['(max-width: 400px)']) {
          this.filterContainer = 'modal-filters-container';
          this.isMobile = true;
          this.isMobileSearch = true;
          return 400;
        }
        if (state.breakpoints['(max-width: 768px)']) {
          this.filterContainer = 'modal-filters-container';
          this.isMobile = true;
          this.isMobileSearch = false;
          return 768;
        }
        this.isMobile = false;
        this.isMobileSearch = false;
        if (state.breakpoints['(max-width: 1070px)' || state.breakpoints['(max-height: 800px)']]) {
          this.filterContainer = 'modal-filters-container';
          return 1070;
        }
        if (state.breakpoints['(max-width: 1650px)']) {
          this.filterOverlayPosition = this.getOverlayPosition(310, -90);
          return 1650;
        }
        this.filterOverlayPosition = this.getOverlayPosition(60);
        this.filterContainer = 'filter-container';
        return 1651;
      })
    );

  public categoriesFilterObserver$ = this.categoriesFilterControl.valueChanges.pipe(
    tap((categoryIds: number[]) => {
      if (!this.isMobile) {
        this.categoryClick(categoryIds);
      }
    })
  );

  public providersFilterObserver$ = this.providersFilterControl.valueChanges.pipe(
    tap((categoryIds: number[]) => {
      if (!this.isMobile) {
        this.providerClick(categoryIds);
      }
    })
  );

  constructor(
    private store: Store,
    private breakpointObserver: BreakpointObserver,
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private translateService: TranslateService,
    private dialog: MatDialog
  ) {}

  private getSetsWord() {
    const word = this.translateService.instant('DATA.TABLE_FILTER.SETS');
    return word[0].toUpperCase() + word.substring(1);
  }

  public getOverlayPosition(offsetX: number, offsetY = 7): ConnectedPosition[] {
    return [
      {
        originX: 'center',
        originY: 'bottom',
        overlayX: 'center',
        overlayY: 'top',
        offsetX: offsetX,
        offsetY: offsetY
      }
    ];
  }

  public makeTranslateAndFilterObserver(): Observable<[CatalogFilter, LangChangeEvent]> {
    return combineLatest([
      this.catalogFilter$,
      this.translateService.onLangChange.pipe(
        startWith({
          lang: '',
          translations: ''
        })
      )
    ]);
  }

  public ngOnInit(): void {
    const filterParams = this.store.selectSnapshot(CatalogTableState.getCatalogFilter);

    const hasFilter = this.store.selectSnapshot(CatalogTableState.hasFilters);
    if (!hasFilter && filterParams) {
      setLocationQueryParams<CatalogQueryParams>(
        {
          ...filterParams.booleanFilters
        },
        this.router,
        this.location
      );
    }

    if (
      hasFilter &&
      filterParams &&
      (Object.keys(filterParams).length !== 0 || (filterParams.searchString && filterParams.searchString?.length > 0))
    ) {
      setLocationQueryParams<CatalogQueryParams>(
        {
          searchString: filterParams?.searchString,
          providerIds: filterParams?.providerIds?.join(','),
          openCategoryIds: filterParams?.openCategoryIds?.join(','),
          ...filterParams.booleanFilters
        },
        this.router,
        this.location
      );
    } else {
      const filterQueryParams = this.route.snapshot.queryParams as CatalogQueryParams;
      const booleanFilters = cloneDeep(filterParams?.booleanFilters) || {};
      const booleanFilterKeys = [
        'datasetType',
        'activeStatus',
        'archiveStatus',
        'accessibility',
        'summerSeason',
        'winterSeason',
        'allSeasons',
        'withMap',
        'withoutMap',
        'iso',
        'ordinaryDict',
        'regionalDict',
        'federalDict'
      ];

      Object.keys(filterQueryParams).forEach(key => {
        if (booleanFilterKeys.includes(key)) {
          booleanFilters[key] = filterQueryParams[key];
        }
      });

      const oldFilters = this.store.selectSnapshot(CatalogTableState.getCatalogFilter);

      const filter: CatalogFilter = {
        newRecommendedFavorites: filterQueryParams.newRecommendedFavorites,
        searchString: oldFilters?.searchString || filterQueryParams.searchString,
        providerIds: filterQueryParams.providerIds ? filterQueryParams.providerIds.split(',').map(val => +val) : [],
        openCategoryIds: filterQueryParams.openCategoryIds ? filterQueryParams.openCategoryIds.split(',').map(val => +val) : [],
        booleanFilters
      };

      this.store.dispatch(new ChangeCatalogFilter(filter, false));
    }

    this.catalogFilter$.pipe(takeUntil(this.componentDestroy())).subscribe(val => {
      if (val && val.newRecommendedFavorites) {
        this.newFavoriteRecommendedToggle.setValue(val.newRecommendedFavorites);
        this._buttonTogglePrevValue = val.newRecommendedFavorites;
      }
    });
  }

  public ngAfterViewInit(): void {
    this.tableHeaderLoaded.emit(true);
  }

  public categoryClick(categoryIds: number[]): void {
    this.store.dispatch(
      new ChangeCatalogFilter(
        {
          openCategoryIds: categoryIds
        },
        true
      )
    );
  }

  public providerClick(providerIds: number[]): void {
    this.store.dispatch(
      new ChangeCatalogFilter(
        {
          providerIds: providerIds
        },
        true
      )
    );
  }

  public categoryAndProvidersFilterClear(): void {
    this.store.dispatch(
      new ChangeCatalogFilter(
        {
          openCategoryIds: [],
          providerIds: []
        },
        true
      )
    );
  }

  public startSearch(value: StartSearch): void {
    this.showMobileSearch = true;
    this.isMobileInputShowed = false;
    this.store.dispatch(
      new ChangeCatalogFilter(
        {
          searchString: value.searchString
        },
        true
      )
    );
  }

  public searchTextChange($event: string): void {
    this.store.dispatch(
      new ChangeCatalogFilter(
        {
          searchString: $event
        },
        false
      )
    );
  }

  public closeCatalogProviderFilterClear(): void {
    this.isOpenCategoryFilter = false;
    this.catalogProviderFilterRef?.forEach(comp => {
      comp.clearAll();
    });
    this.store.dispatch(
      new ChangeCatalogFilter(
        {
          providerIds: [],
          openCategoryIds: []
        },
        true
      )
    );
  }

  public closeCatalogProviderFilterSubmit(): void {
    this.isOpenCategoryFilter = false;
    this.catalogProviderFilterRef?.forEach(comp => {
      comp.triggerWriteValue();
    });

    this.store.dispatch(
      new ChangeCatalogFilter(
        {
          providerIds: this.providersFilterControl.value,
          openCategoryIds: this.categoriesFilterControl.value
        },
        true
      )
    );
  }

  public clearFilters(): void {
    this.store.dispatch(
      new ChangeCatalogFilter(
        {
          catalogIds: [],
          booleanFilters: {
            activeStatus: true,
            archiveStatus: null,
            withMap: null,
            withoutMap: null,
            iso: null,
            summerSeason: null,
            regionalDict: null,
            ordinaryDict: null,
            federalDict: null,
            datasetType: null,
            allSeasons: null,
            accessibility: null,
            winterSeason: null
          },
          openCategoryIds: [],
          providerIds: [],
          newRecommendedFavorites: 'all'
        },
        true
      )
    );
    this.newFavoriteRecommendedToggle.setValue('all');
    this._buttonTogglePrevValue = 'all';
  }

  public onNewFavoriteRecommendedChange(): void {
    const type = this.newFavoriteRecommendedToggle.value as HandleCatalogHeaderToggleModel;
    const user = this.store.selectSnapshot(AuthState.authenticatedUser);

    if (!user && type !== 'new' && type !== 'all') {
      const authorizationDialog = this.dialog.open(AuthorizationDialogComponent, { disableClose: true });
      authorizationDialog.componentInstance.keyOperations = 'NewRecommendedFavoriteToggleState';
      authorizationDialog.componentInstance.operationValue = { type };
      this.newFavoriteRecommendedToggle.setValue(this._buttonTogglePrevValue);
      if (this.mobileFav) this.mobileFav.nativeElement.checked = false;
      return;
    }
    this._buttonTogglePrevValue = type;
    this.store.dispatch(new HandleCatalogHeaderToggle(type));
  }

  public onMobileFavorites(event: Event): void {
    const isChecked = (event.target as HTMLInputElement).checked;
    if (isChecked) {
      this.newFavoriteRecommendedToggle.setValue('favorites');
      this._buttonTogglePrevValue = 'favorites';
      this.onNewFavoriteRecommendedChange();
    } else {
      this.newFavoriteRecommendedToggle.setValue('all');
      this._buttonTogglePrevValue = 'all';
      this.onNewFavoriteRecommendedChange();
    }
  }
}
